import { useAuth } from '../auth/AuthProvider';

export class AzureFunctionService {
  private baseUrl: string;
  private getAccessToken: (scopes: string[]) => Promise<string>;

  constructor(baseUrl: string, getAccessToken: (scopes: string[]) => Promise<string>) {
    this.baseUrl = baseUrl;
    this.getAccessToken = getAccessToken;
  }

  async callFunction(functionName: string, data?: any): Promise<any> {
    try {
      // Get access token for your Azure Function API
      // Replace with your Azure Function's scope/audience
      const token = await this.getAccessToken(['api://b547acaf-ebac-4895-936a-b8110a6a39cb/user_impersonation']);
      
      const response = await fetch(`${this.baseUrl}/api/${functionName}`, {
        method: data ? 'POST' : 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.text();
      return result;
    } catch (error) {
      console.error('Azure Function call error:', error);
      throw error;
    }
  }

  async downloadCsv(functionName: string, data: any): Promise<void> {
    try {
      // Get access token for your Azure Function API
      const token = await this.getAccessToken(['api://b547acaf-ebac-4895-936a-b8110a6a39cb/user_impersonation']);
      
      // Build query parameters from the data object
      const queryParams = new URLSearchParams();
      Object.keys(data).forEach(key => {
        queryParams.append(key, data[key]);
      });
      
      const response = await fetch(`${this.baseUrl}/api/${functionName}?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the CSV content
      const csvContent = await response.text();
      
      // Create a blob and download it
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${data.payGroup}-harri-pds-${data.startDate}-to-${data.endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV download error:', error);
      throw error;
    }
  }

  async getCompanies(): Promise<any[]> {
    try {
      // Get access token for your Azure Function API
      const token = await this.getAccessToken(['api://b547acaf-ebac-4895-936a-b8110a6a39cb/user_impersonation']);
      
      const response = await fetch(`${this.baseUrl}/api/ukg/companies`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const companies = await response.json();
      return companies;
    } catch (error) {
      console.error('Get companies error:', error);
      throw error;
    }
  }

  async getStats(functionName: string, data: any): Promise<any> {
    try {
      // Get access token for your Azure Function API
      const token = await this.getAccessToken(['api://b547acaf-ebac-4895-936a-b8110a6a39cb/user_impersonation']);
      
      // Build query parameters from the data object
      const queryParams = new URLSearchParams();
      Object.keys(data).forEach(key => {
        queryParams.append(key, data[key]);
      });
      
      const response = await fetch(`${this.baseUrl}/api/${functionName}?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 204) {
        // No content - return null to indicate no data found
        return null;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const stats = await response.json();
      return stats;
    } catch (error) {
      console.error('Get stats error:', error);
      throw error;
    }
  }
}

export const useAzureFunctionService = () => {
  const { getAccessToken } = useAuth();
  
  // Replace with your actual Azure Function URL
  const functionAppUrl = process.env.REACT_APP_FUNCTION_URL || 'https://harri-tools-fwgddpg6btgqdjee.westus3-01.azurewebsites.net';

  return new AzureFunctionService(functionAppUrl, getAccessToken);
};